/**
 * PAPPO — Auth Service
 * Funções de autenticação: login, register, logout, refresh.
 * Usa o api.ts wrapper para comunicação com o backend.
 */

import { apiPost } from './api';
import type { LoginPayload, RegisterPayload } from '../types/user';
import type { LoginResponse } from '../types/api';

/**
 * Registar novo utilizador (cadastro só por convite). O backend devolve tokens
 * (mesmo formato do login) — guardamos para já entrar na tela de verificação.
 */
export async function register(payload: RegisterPayload) {
  const result = await apiPost<LoginResponse>('/auth/register', payload, {
    noAuthRedirect: true,
  });

  if (result.success && result.data) {
    localStorage.setItem('pappo_access_token', result.data.access_token);
    localStorage.setItem('pappo_refresh_token', result.data.refresh_token);
    localStorage.setItem('pappo_user', JSON.stringify(result.data.user));
  }

  return result;
}

/**
 * Login — guarda tokens no localStorage
 */
export async function login(payload: LoginPayload) {
  const result = await apiPost<LoginResponse>('/auth/login', payload, {
    noAuthRedirect: true,
  });

  if (result.success && result.data) {
    localStorage.setItem('pappo_access_token', result.data.access_token);
    localStorage.setItem('pappo_refresh_token', result.data.refresh_token);
    localStorage.setItem('pappo_user', JSON.stringify(result.data.user));
  }

  return result;
}

/**
 * Refresh do access token
 */
export async function refreshToken() {
  const refresh = localStorage.getItem('pappo_refresh_token');
  if (!refresh) return { success: false, error: 'No refresh token' };

  return apiPost<{ access_token: string }>('/auth/refresh', {
    refresh_token: refresh,
  });
}

/**
 * Esqueci a senha — pede o link de redefinição por email. Resposta sempre
 * genérica (o backend não revela se o email existe).
 */
export async function forgotPassword(email: string) {
  return apiPost<{ message: string }>('/auth/forgot-password', { email });
}

/**
 * Redefinir a senha a partir do token recebido por email.
 */
export async function resetPassword(token: string, password: string) {
  return apiPost<{ message?: string; error?: string }>('/auth/reset-password', {
    token,
    password,
  });
}

/**
 * Logout — limpa tokens
 */
export function logout() {
  localStorage.removeItem('pappo_access_token');
  localStorage.removeItem('pappo_refresh_token');
  localStorage.removeItem('pappo_user');
  window.location.href = '/login';
}

/**
 * Retorna o utilizador actual do localStorage (sem fetch)
 */
export function getCurrentUser() {
  const userStr = localStorage.getItem('pappo_user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

/**
 * Verifica se o utilizador está autenticado (tem token)
 */
export function isAuthenticated(): boolean {
  return !!localStorage.getItem('pappo_access_token');
}
